/*
 * Suivi des prélèvements (todolist → Facturation des consommateurs → Domiciliation) :
 * fichiers XML générés (retéléchargement) et statut de chaque facture, marqué à la main
 * (Encaissée, Rejetée, Remboursée). Alerte BNP : plus de 30 % de rejets dans un fichier d'au moins 10 prélèvements.
 */
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { AlertTriangle, ChevronDown, ChevronRight, FileDown, Loader2, RefreshCw } from 'lucide-react';
import {
  type BillingInvoice, type BillingStatus, type SepaBatch, STATUS_LABELS, downloadSepaFile, fetchBatchInvoices, fetchSepaBatches,
  formatEuro, setInvoiceStatus,
} from './billing';
import { brusselsNow } from '../../../supabase/functions/_shared/sepa';
import { errorMessage, formatDateFr } from './ui';

const STATUS_STYLES: Record<BillingStatus, string> = {
  prepared: 'bg-neutral-100 text-neutral-600',
  sent: 'bg-neutral-100 text-neutral-600',
  to_collect: 'bg-amber-100 text-amber-800',
  collected: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-700',
  refunded: 'bg-violet-100 text-violet-700',
};
const MARKS: BillingStatus[] = ['collected', 'rejected', 'refunded'];
const MARK_LABELS: Record<string, string> = { collected: 'Encaissé', rejected: 'Rejeté', refunded: 'Remboursé' };

export function SepaFollowUp({ refreshKey }: { refreshKey: number }) {
  const [batches, setBatches] = useState<SepaBatch[] | null>(null);
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    try {
      const list = await fetchSepaBatches();
      setBatches(list);
      setInvoices(await fetchBatchInvoices(list.map(b => b.id)));
    } catch (err) {
      toast.error(`Suivi des prélèvements : ${errorMessage(err)}`);
      setBatches([]);
    }
  };
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const mark = async (ids: string[], status: BillingStatus, key: string) => {
    setBusy(key);
    try {
      const date = brusselsNow().date;
      await setInvoiceStatus(ids, status, date);
      setInvoices(prev => prev.map(i => (ids.includes(i.id) ? { ...i, status, status_date: date } : i)));
      toast.success(`${ids.length} facture${ids.length > 1 ? 's' : ''} : ${STATUS_LABELS[status].toLowerCase()}`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const download = async (b: SepaBatch) => {
    setBusy(`dl-${b.id}`);
    try {
      await downloadSepaFile(b);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  if (batches === null) return <p className="flex items-center gap-1.5 px-4 py-3 text-[11px] text-neutral-400"><Loader2 className="w-3.5 h-3.5 animate-spin" />Chargement du suivi…</p>;

  return (
    <div className="px-4 py-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold text-neutral-600 uppercase tracking-wide">Fichiers de domiciliation ({batches.length})</p>
        <button onClick={load} className="flex items-center gap-1 text-[11px] text-neutral-500 hover:text-neutral-900"><RefreshCw className="w-3 h-3" />Actualiser</button>
      </div>
      {batches.length === 0 && <p className="text-[11px] text-neutral-400">Aucun fichier généré.</p>}
      <ul className="space-y-1.5">
        {batches.map(b => {
          const list = invoices.filter(i => i.sepa_batch_id === b.id);
          const rejected = list.filter(i => i.status === 'rejected').length;
          const toCollect = list.filter(i => i.status === 'to_collect');
          const rate = list.length ? rejected / list.length : 0;
          const alert = list.length >= 10 && rate > 0.3;
          const isOpen = open === b.id;
          return (
            <li key={b.id} className={`rounded-lg border ${alert ? 'border-red-300' : 'border-neutral-200'} bg-white`}>
              <div className="flex items-center gap-2 px-3 py-2">
                <button onClick={() => setOpen(isOpen ? null : b.id)} className="flex items-center gap-1.5 min-w-0 flex-1 text-left">
                  {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-neutral-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-neutral-400 shrink-0" />}
                  <span className="min-w-0">
                    <span className="block text-xs font-medium text-neutral-900 truncate">
                      Prélèvement du {formatDateFr(b.collection_date)} · {b.tx_count} facture{b.tx_count > 1 ? 's' : ''} · {formatEuro(Number(b.total))}
                    </span>
                    <span className="block text-[10px] text-neutral-500 truncate">
                      {b.file_name} · créé le {formatDateFr(b.created_at)}
                      {b.import_deadline && <> · import avant le {new Date(b.import_deadline).toLocaleString('fr-BE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</>}
                      {' · '}{toCollect.length} à encaisser{rejected > 0 && ` · ${rejected} rejet${rejected > 1 ? 's' : ''} (${Math.round(rate * 100)} %)`}
                    </span>
                  </span>
                </button>
                {alert && <span className="flex items-center gap-1 text-[10px] font-semibold text-red-700"><AlertTriangle className="w-3.5 h-3.5" />Seuil BNP de 30 % dépassé</span>}
                <button onClick={() => download(b)} disabled={busy === `dl-${b.id}`}
                  className="flex items-center gap-1 px-2 py-1 rounded-md border border-neutral-200 text-[11px] text-neutral-700 hover:bg-neutral-50 disabled:opacity-50">
                  {busy === `dl-${b.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}XML
                </button>
              </div>
              {isOpen && (
                <div className="border-t border-neutral-100 px-3 py-2 space-y-1.5">
                  {toCollect.length > 0 && (
                    <button onClick={() => mark(toCollect.map(i => i.id), 'collected', `all-${b.id}`)} disabled={!!busy}
                      className="text-[11px] font-semibold text-emerald-700 hover:underline disabled:opacity-50">
                      Tout marquer encaissé ({toCollect.length})
                    </button>
                  )}
                  <ul className="divide-y divide-neutral-100">
                    {list.map(i => (
                      <li key={i.id} className="flex items-center gap-2 py-1.5">
                        <span className="min-w-0 flex-1">
                          <span className="block text-[11px] text-neutral-900 truncate">
                            <span className="font-mono">{i.invoice_number}</span> · {i.title} · {formatEuro(Number(i.amount))}
                            <span className="text-neutral-400"> · {i.sequence_type} · {i.mandate_reference}</span>
                          </span>
                        </span>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${STATUS_STYLES[i.status]}`}>
                          {STATUS_LABELS[i.status]}{i.status_date && i.status !== 'to_collect' ? ` le ${formatDateFr(i.status_date)}` : ''}
                        </span>
                        <span className="flex gap-1">
                          {MARKS.filter(m => m !== i.status).map(m => (
                            <button key={m} onClick={() => mark([i.id], m, `${i.id}-${m}`)} disabled={!!busy}
                              className="px-1.5 py-0.5 rounded border border-neutral-200 text-[10px] text-neutral-600 hover:bg-neutral-50 disabled:opacity-50">
                              {MARK_LABELS[m]}
                            </button>
                          ))}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
